<template>
  <section>
    <sme-card v-if="loading">
      <app-loading />
    </sme-card>

    <template v-else-if="hasData">
      <b-row class="mb-4">
        <b-col v-for="card in kpiCards" :key="card.label" cols="12" md="6" class="mb-3 mb-md-0">
          <app-stat
            :stat="card.value"
            :formatter="toNumber"
            suffix="%"
            :description="card.label"
            :background-color="card.backgroundColor"
            class="h-100"
          >
            <span class="safeguarding-kpi__sub">of employees</span>
          </app-stat>
        </b-col>
      </b-row>

      <page-sub-header title="Safeguarding" subtitle="Tiered support across your enrolled base">
        <template #extra-title>
          <router-link
            v-if="canViewHelpCenter"
            :to="{ name: 'help-center', params: { slug: SAFEGUARDING_HELP_SLUG } }"
            class="safeguarding-help"
            title="What is safeguarding?"
            aria-label="What is safeguarding?"
          >
            <font-awesome-icon :icon="['fad', 'info-circle']" />
          </router-link>
        </template>
        <template #header>
          <app-input
            v-model="view"
            :options="viewOptions"
            type="radio"
            buttons
            button-variant="outline-primary"
            size="sm"
            class="mb-0"
          />
        </template>
      </page-sub-header>

      <sme-card v-if="view === 'Today'">
        <h2 class="safeguarding-card__title">Where your employees are today</h2>
        <p class="safeguarding-card__subtitle">Share of monitored employees in each support tier</p>

        <b-row class="align-items-center">
          <b-col cols="12" md="5" lg="4" class="mb-4 mb-md-0 d-flex justify-content-center">
            <div class="safeguarding-donut">
              <donut-chart
                :labels="donutLabels"
                :values="donutValues"
                :colors="donutColors"
                :show-data-labels="false"
                :chart-options="{ aspectRatio: 1 }"
              />
            </div>
          </b-col>
          <b-col cols="12" md="7" lg="8">
            <div v-for="tier in mapped.tiers" :key="tier.key" class="safeguarding-legend__row">
              <span class="safeguarding-legend__dot" :style="{ backgroundColor: tier.color }" />
              <div class="safeguarding-legend__meta">
                <p class="safeguarding-legend__name">{{ tier.label }}</p>
                <p class="safeguarding-legend__desc">{{ tier.description }}</p>
              </div>
              <span class="safeguarding-legend__pct">{{ toNumber(tier.percent) }}%</span>
            </div>
          </b-col>
        </b-row>

        <div class="safeguarding-note">
          <font-awesome-icon :icon="['fad', 'calendar-clock']" class="safeguarding-note__icon" />
          <span
            >Trends over time appear once we have a full month of data. Until then, this is your starting point.</span
          >
        </div>
      </sme-card>

      <sme-card v-else class="safeguarding-empty-trends text-center">
        <div class="safeguarding-empty-trends__icon">
          <font-awesome-icon :icon="['fad', 'calendar-clock']" />
        </div>
        <p class="safeguarding-empty-trends__title">Trends coming soon</p>
        <p class="safeguarding-empty-trends__body">
          Trends over time appear once we have a full month of data. Until then, this is your starting point.
        </p>
      </sme-card>
    </template>

    <sme-card v-else class="text-center safeguarding-empty">
      <p class="mb-0">No safeguarding data available for this company yet.</p>
    </sme-card>
  </section>
</template>

<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import ApiClient from '@/ApiClient';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppStat from '@/components/AppStat.vue';
import PageSubHeader from '@/components/PageSubHeader.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import DonutChart from '@/components/charts/DonutChart.vue';
import { mapSafeguardingBreakdown } from '@/pages/analyticsSafeguarding/safeguardingData';
import State from '@/state/State';
import { toNumber } from '@/utils/common';

const SAFEGUARDING_HELP_SLUG = 'safeguarding';

const loading = ref(true);
const breakdown = ref(null);
const view = ref('Today');
const viewOptions = ['Today', '6 months'];

const mapped = computed(() => mapSafeguardingBreakdown(breakdown.value));
const hasData = computed(() => !loading.value && mapped.value.hasData);
const canViewHelpCenter = computed(() => !!State.state.company?.properties?.portal?.can_view_help_center);

const kpiCards = computed(() => [
  { label: 'No elevated signs of strain', value: mapped.value.basePercent, backgroundColor: 'core-2-lighten-20' },
  { label: 'In active support', value: mapped.value.activePercent, backgroundColor: 'core-4' },
]);

const donutLabels = computed(() => mapped.value.tiers.map(tier => tier.label));
const donutValues = computed(() => mapped.value.tiers.map(tier => tier.percent));
const donutColors = computed(() => mapped.value.tiers.map(tier => tier.color));

const getMetrics = async () => {
  loading.value = true;
  try {
    const response = await ApiClient.getCompanySafeguardingTiers();
    breakdown.value = response?.data ?? null;
  } catch {
    breakdown.value = null;
  } finally {
    loading.value = false;
  }
};

onBeforeMount(() => getMetrics());

function getExportTable() {
  if (!hasData.value) {
    return null;
  }
  return {
    headers: ['Tier', 'Employees', 'Percentage (%)'],
    rows: mapped.value.tiers.map(tier => [tier.label, tier.count, tier.percent]),
  };
}

function getPdfData() {
  return {
    topKpis: kpiCards.value.map(card => ({
      label: card.label,
      value: `${toNumber(card.value)}%`,
      sub: 'of employees',
    })),
  };
}

defineExpose({ hasData, loading, getExportTable, getPdfData });
</script>

<style lang="scss" scoped>
.safeguarding-kpi__sub {
  display: block;
  font-size: 14px;
  font-weight: 400;
  opacity: 0.92;
}

.safeguarding-help {
  align-items: center;
  color: var(--palette-color-default-lighten-30);
  display: inline-flex;
  justify-content: center;
  margin-left: 0.5rem;

  &:hover {
    color: var(--palette-color-brand-primary);
  }
}

.safeguarding-card__title {
  color: var(--palette-color-default);
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.01em;
  margin: 0;
}

.safeguarding-card__subtitle {
  color: var(--palette-color-default-lighten-20);
  font-size: 13.5px;
  margin: 0 0 1.5rem;
}

.safeguarding-donut {
  width: 100%;
  max-width: 260px;
}

.safeguarding-legend__row {
  align-items: center;
  border-top: 1px solid var(--palette-color-default-lighten-90);
  display: grid;
  gap: 0.875rem;
  grid-template-columns: 12px minmax(0, 1fr) auto;
  padding: 0.875rem 0.125rem;
}

.safeguarding-legend__dot {
  border-radius: 3px;
  height: 12px;
  width: 12px;
}

.safeguarding-legend__meta {
  min-width: 0;
}

.safeguarding-legend__name {
  color: var(--palette-color-default);
  font-size: 15px;
  font-weight: 700;
  margin: 0;
}

.safeguarding-legend__desc {
  color: var(--palette-color-default-lighten-20);
  font-size: 13px;
  margin: 0.125rem 0 0;
}

.safeguarding-legend__pct {
  color: var(--palette-color-default);
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
  text-align: right;
}

.safeguarding-note {
  align-items: center;
  background: var(--palette-color-base-background);
  border-radius: 8px;
  color: var(--palette-color-default-lighten-20);
  display: flex;
  font-size: 13.5px;
  gap: 0.625rem;
  margin-top: 1.5rem;
  padding: 0.8125rem 1rem;
}

.safeguarding-note__icon {
  color: var(--palette-color-brand-primary);
  flex: 0 0 auto;
}

.safeguarding-empty-trends {
  padding: 4.5rem 2.5rem;
}

.safeguarding-empty-trends__icon {
  align-items: center;
  background: var(--palette-color-core-1-lighten-90);
  border-radius: 999px;
  color: var(--palette-color-brand-primary);
  display: inline-flex;
  font-size: 26px;
  height: 56px;
  justify-content: center;
  margin: 0 auto 1.125rem;
  width: 56px;
}

.safeguarding-empty-trends__title {
  color: var(--palette-color-default);
  font-size: 20px;
  font-weight: 800;
  margin: 0;
}

.safeguarding-empty-trends__body {
  color: var(--palette-color-default-lighten-20);
  font-size: 14px;
  line-height: 1.5;
  margin: 0.5rem auto 0;
  max-width: 420px;
}

.safeguarding-empty {
  color: var(--palette-color-default-lighten-20);
  padding: 4rem 1rem;
}
</style>
